/**
 * Season Management Page (Admin Only)
 * 
 * Manages seasons and season rollover:
 * - Display current season and available seasons
 * - Implement season rollover with confirmation
 * - Show progress indicators
 * - Admin-only access restriction
 * 
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5
 */

import { useEffect, useState, useCallback } from 'react';
import { restClient } from '../../api/rest-client';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Modal } from '../../components/common/Modal';

/**
 * Season Configuration Interface
 */
interface SeasonConfig {
  currentSeason: string;
  availableSeasons: string[];
}

/**
 * Season Rollover Confirmation Modal
 */
interface RolloverConfirmationProps {
  isOpen: boolean;
  currentSeason: string;
  newSeason: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function RolloverConfirmation({
  isOpen,
  currentSeason,
  newSeason,
  onConfirm,
  onCancel,
  isLoading,
}: RolloverConfirmationProps) {
  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Confirm Season Rollover">
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <svg
              className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Warning</h3>
              <p className="mt-1 text-sm text-yellow-700">
                This action will create a new season and cannot be undone.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-gray-700">
            You are about to roll over from season <strong>{currentSeason}</strong> to season{' '}
            <strong>{newSeason}</strong>.
          </p>
          <p className="text-sm text-gray-600">
            This will:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-4">
            <li>Set {newSeason} as the current season</li>
            <li>Create new season data structures</li>
            <li>Preserve all historical data from {currentSeason}</li>
          </ul>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? 'Processing...' : 'Confirm Rollover'}
          </Button>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Season Rollover Form
 */
interface RolloverFormProps {
  currentSeason: string;
  onSubmit: (newSeason: string) => void;
}

function RolloverForm({ currentSeason, onSubmit }: RolloverFormProps) {
  const [newSeason, setNewSeason] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate new season
    if (!newSeason.trim()) {
      setError('Season is required');
      return;
    }

    // Validate format (should be a year)
    if (!/^\d{4}$/.test(newSeason)) {
      setError('Season must be a 4-digit year (e.g., 2026)');
      return;
    }

    const year = parseInt(newSeason, 10);
    const currentYear = parseInt(currentSeason, 10);

    if (year <= currentYear) {
      setError(`New season must be after current season (${currentSeason})`);
      return;
    }

    if (year > currentYear + 5) {
      setError('New season cannot be more than 5 years in the future');
      return;
    }

    setError('');
    onSubmit(newSeason);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="newSeason" className="block text-sm font-medium text-gray-700 mb-1">
          New Season (Year) *
        </label>
        <input
          type="text"
          id="newSeason"
          value={newSeason}
          onChange={(e) => {
            setNewSeason(e.target.value);
            setError('');
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={`${parseInt(currentSeason, 10) + 1}`}
          maxLength={4}
        />
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        <p className="mt-1 text-xs text-gray-500">
          Enter the year for the new season (e.g., {parseInt(currentSeason, 10) + 1})
        </p>
      </div>

      <Button type="submit" variant="primary" className="w-full">
        Initiate Rollover
      </Button>
    </form>
  );
}

/**
 * Season Management Page Component
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5
 */
export function SeasonManagement() {
  // State
  const [seasonConfig, setSeasonConfig] = useState<SeasonConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingNewSeason, setPendingNewSeason] = useState('');
  const [isRollingOver, setIsRollingOver] = useState(false);
  const [rolloverProgress, setRolloverProgress] = useState<string | null>(null);
  const [rolloverSuccess, setRolloverSuccess] = useState(false);

  /**
   * Fetch season configuration
   * Requirement 9.1
   */
  const fetchSeasonConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const config = await restClient.getSeasonConfig();
      setSeasonConfig(config);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load season configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeasonConfig();
  }, []);

  /**
   * Handle rollover initiation
   * Requirement 9.2
   */
  const handleInitiateRollover = useCallback((newSeason: string) => {
    setPendingNewSeason(newSeason);
    setIsConfirmModalOpen(true);
  }, []);

  /**
   * Handle rollover confirmation
   * Requirements: 9.2, 9.4, 9.5
   */
  const handleConfirmRollover = useCallback(async () => {
    if (!pendingNewSeason) return;

    setIsRollingOver(true);
    setRolloverProgress('Initiating season rollover...');
    setError(null);
    setRolloverSuccess(false);

    try {
      // Simulate progress updates (in real implementation, this would come from backend)
      setRolloverProgress('Creating new season data structures...');
      
      await restClient.rolloverSeason(pendingNewSeason);

      setRolloverProgress('Season rollover completed successfully!');
      setRolloverSuccess(true);

      // Refresh season config
      await fetchSeasonConfig();

      // Close modal after a delay
      setTimeout(() => {
        setIsConfirmModalOpen(false);
        setPendingNewSeason('');
        setRolloverProgress(null);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Season rollover failed');
      setRolloverProgress(null);
    } finally {
      setIsRollingOver(false);
    }
  }, [pendingNewSeason]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Season Management</h1>
        <p className="text-gray-600">Manage seasons and perform season rollover</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {rolloverSuccess && !isConfirmModalOpen && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <svg
              className="h-5 w-5 text-green-400 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-green-800">Season rollover completed successfully!</p>
          </div>
        </div>
      )}

      {seasonConfig && (
        <div className="space-y-6">
          {/* Current Season Card */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Current Season</h2>
              <span className="inline-block px-4 py-2 text-lg font-bold text-white bg-primary-500 rounded-full">
                {seasonConfig.currentSeason}
              </span>
            </div>
            <p className="text-gray-600">
              This is the active season for all new events and riders.
            </p>
          </Card>

          {/* Available Seasons Card */}
          <Card>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Available Seasons</h2>
            <div className="flex flex-wrap gap-2">
              {seasonConfig.availableSeasons.length > 0 ? (
                seasonConfig.availableSeasons.map((season) => (
                  <span
                    key={season}
                    className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                      season === seasonConfig.currentSeason
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {season}
                  </span>
                ))
              ) : (
                <p className="text-gray-500">No seasons available</p>
              )}
            </div>
            <p className="mt-4 text-sm text-gray-600">
              Historical data is preserved for all seasons.
            </p>
          </Card>

          {/* Season Rollover Card */}
          <Card>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Season Rollover</h2>
            <p className="text-gray-600 mb-6">
              Create a new season and set it as the current season. This will preserve all
              historical data from previous seasons.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
              <div className="flex">
                <svg
                  className="h-5 w-5 text-blue-400 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Important Information</h3>
                  <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
                    <li>Season rollover is a one-time operation that cannot be undone</li>
                    <li>All historical data will be preserved</li>
                    <li>New events and riders will use the new season by default</li>
                    <li>Users can still view and filter data from previous seasons</li>
                  </ul>
                </div>
              </div>
            </div>

            <RolloverForm
              currentSeason={seasonConfig.currentSeason}
              onSubmit={handleInitiateRollover}
            />
          </Card>

          {/* Progress Indicator */}
          {rolloverProgress && (
            <Card>
              <div className="flex items-center">
                <LoadingSpinner size="sm" />
                <p className="ml-3 text-gray-700">{rolloverProgress}</p>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Rollover Confirmation Modal */}
      {seasonConfig && (
        <RolloverConfirmation
          isOpen={isConfirmModalOpen}
          currentSeason={seasonConfig.currentSeason}
          newSeason={pendingNewSeason}
          onConfirm={handleConfirmRollover}
          onCancel={() => {
            setIsConfirmModalOpen(false);
            setPendingNewSeason('');
          }}
          isLoading={isRollingOver}
        />
      )}
    </div>
  );
}

export default SeasonManagement;
